import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Phone, Scissors, Bath, Check, X, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import appApi from "./appApi";
import { Skeleton, stagger, rise, spring } from "./bits";
import ConsentScreen from "./ConsentScreen";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-700",
  confirmed: "bg-sky-100 text-sky-700",
  done: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-rose-100 text-rose-600",
  no_show: "bg-stone-200 text-stone-500",
};

const pad2 = (n) => String(n).padStart(2, "0");
const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const BookingCard = ({ b, statuses, canManage, onAction }) => {
  const isGroom = b.type === "grooming";
  return (
    <motion.div
      variants={rise}
      layout
      className="rounded-2xl bg-white border border-brand/10 p-4 shadow-[0_6px_24px_rgba(134,77,99,0.07)]"
      data-testid={`app-booking-${b.id}`}
    >
      <div className="flex items-center gap-3">
        <div className={`grid place-items-center h-11 w-11 rounded-xl shrink-0 ${isGroom ? "bg-brand-soft text-brand" : "bg-sky-50 text-sky-600"}`}>
          {isGroom ? <Scissors className="h-5 w-5" /> : <Bath className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-brand-ink truncate">{b.customer_name}</p>
          <p className="text-xs text-brand-ink/55 truncate">
            {isGroom ? `${b.pet_name || "—"}${b.pet_breed ? ` · ${b.pet_breed}` : ""}${b.service ? ` · ${b.service}` : ""}` : `${b.plan}${b.nagayu ? " + Nagayu" : ""}${b.total_price ? ` · €${b.total_price}` : ""}`}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-heading text-lg font-bold text-brand">{b.time}</p>
          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_STYLE[b.status] || STATUS_STYLE.pending}`}>
            {statuses[b.status] || b.status}
          </span>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <a href={`tel:${b.phone}`} className="inline-flex items-center gap-1.5 text-xs font-bold text-brand bg-brand-soft/60 rounded-full px-3 py-1.5 no-a11y-underline" data-testid={`app-call-${b.id}`}>
          <Phone className="h-3.5 w-3.5" /> {b.phone}
        </a>
        {canManage && (b.status === "pending" || b.status === "confirmed") && (
          <div className="ml-auto flex gap-2">
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => onAction(b, "done")}
              className="grid place-items-center h-9 w-9 rounded-full bg-emerald-500 text-white shadow"
              data-testid={`app-done-${b.id}`}
              aria-label="done"
            >
              <Check className="h-4 w-4" />
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => onAction(b, "cancelled")}
              className="grid place-items-center h-9 w-9 rounded-full bg-rose-500 text-white shadow"
              data-testid={`app-cancel-${b.id}`}
              aria-label="cancel"
            >
              <X className="h-4 w-4" />
            </motion.button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Month calendar like the website — dots mark days with bookings, tap a day to view it
const MonthCalendar = ({ month, counts, selected, today, onSelect, onMonth, lang }) => {
  const [y, mo] = month.split("-").map(Number);
  const first = new Date(y, mo - 1, 1);
  const offset = (first.getDay() + 6) % 7; // Monday-first
  const days = new Date(y, mo, 0).getDate();
  const wd = lang === "el" ? ["Δε", "Τρ", "Τε", "Πε", "Πα", "Σα", "Κυ"] : ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
  const label = new Date(y, mo - 1, 1).toLocaleDateString(lang === "el" ? "el-GR" : "en-US", { month: "long", year: "numeric" });

  const shift = (dir) => {
    const d = new Date(y, mo - 1 + dir, 1);
    onMonth(`${d.getFullYear()}-${pad2(d.getMonth() + 1)}`);
  };

  return (
    <div className="rounded-3xl bg-white border border-brand/10 p-4 shadow-[0_10px_34px_rgba(134,77,99,0.10)]" data-testid="app-calendar">
      <div className="flex items-center justify-between mb-3">
        <motion.button whileTap={{ scale: 0.88 }} onClick={() => shift(-1)} className="grid place-items-center h-9 w-9 rounded-full hover:bg-brand-soft/60 text-brand" data-testid="cal-prev" aria-label="prev">
          <ChevronLeft className="h-5 w-5" />
        </motion.button>
        <p className="font-heading text-lg font-bold text-brand-ink capitalize" data-testid="cal-month-label">{label}</p>
        <motion.button whileTap={{ scale: 0.88 }} onClick={() => shift(1)} className="grid place-items-center h-9 w-9 rounded-full hover:bg-brand-soft/60 text-brand" data-testid="cal-next" aria-label="next">
          <ChevronRight className="h-5 w-5" />
        </motion.button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {wd.map((d) => (
          <span key={d} className="text-[10px] font-bold uppercase tracking-wide text-brand-ink/35 py-1">{d}</span>
        ))}
        {Array.from({ length: offset }).map((_, i) => <span key={`b${i}`} />)}
        {Array.from({ length: days }).map((_, i) => {
          const dstr = `${month}-${pad2(i + 1)}`;
          const cnt = counts[dstr] || 0;
          const isSel = dstr === selected;
          const isToday = dstr === today;
          return (
            <motion.button
              key={dstr}
              type="button"
              whileTap={{ scale: 0.85 }}
              transition={spring}
              onClick={() => onSelect(dstr)}
              className={`relative h-10 rounded-xl text-sm font-bold transition-colors ${
                isSel ? "bg-brand text-brand-cream shadow" : isToday ? "bg-brand-soft text-brand" : cnt ? "text-brand-ink hover:bg-brand-soft/50" : "text-brand-ink/45 hover:bg-brand-soft/30"
              }`}
              data-testid={`cal-day-${dstr}`}
            >
              {i + 1}
              {cnt > 0 && (
                <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 flex items-center gap-0.5`}>
                  {Array.from({ length: Math.min(cnt, 3) }).map((_, k) => (
                    <span key={k} className={`h-1 w-1 rounded-full ${isSel ? "bg-brand-cream" : "bg-brand"}`} />
                  ))}
                </span>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

const TodayScreen = ({ user }) => {
  const { t, lang } = useLang();
  const m = t.mapp;
  const statuses = t.admin.statuses;
  const today = localToday();
  const [month, setMonth] = useState(today.slice(0, 7));
  const [selDate, setSelDate] = useState(today);
  const [data, setData] = useState(null);
  const [consentFor, setConsentFor] = useState(null); // booking awaiting photo consent before "done"

  const load = useCallback(() => {
    appApi.get(`/app/schedule?month=${month}`).then((r) => setData(r.data)).catch(() => toast.error(m.error_generic));
  }, [month, m.error_generic]);
  useEffect(() => { setData(null); load(); }, [load]);

  const act = async (b, status) => {
    // Completing a booking always asks the customer for photo consent first (full screen).
    if (status === "done") { setConsentFor(b); return; }
    if (status === "cancelled" && !window.confirm(m.cancel_confirm)) return;
    try {
      await appApi.patch(`/app/bookings/${b.type}/${b.id}/status`, { status });
      toast.success(m.status_cancelled_toast);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || m.error_generic);
    }
  };

  const canManage = !!user.perms?.bookings;
  const dayItems = useMemo(() => (data?.items || []).filter((b) => b.date === selDate), [data, selDate]);
  const selLabel = selDate === today
    ? m.today_label
    : new Date(`${selDate}T12:00:00`).toLocaleDateString(lang === "el" ? "el-GR" : "en-US", { weekday: "long", day: "numeric", month: "long" });

  const pickMonth = (mm) => {
    setMonth(mm);
    setSelDate(mm === today.slice(0, 7) ? today : `${mm}-01`);
  };

  return (
    <div data-testid="app-today-screen">
      <h1 className="font-heading text-3xl font-medium text-brand-ink">{m.tab_today}</h1>
      <div className="mt-5">
        <MonthCalendar month={month} counts={data?.counts || {}} selected={selDate} today={today} onSelect={setSelDate} onMonth={pickMonth} lang={lang} />
      </div>

      <p className="mt-6 text-xs font-bold uppercase tracking-widest text-brand-ink/40" data-testid="app-day-label">
        {selLabel} · {selDate}
      </p>
      {data === null ? (
        <div className="mt-3 space-y-3"><Skeleton className="h-24" /><Skeleton className="h-24" /></div>
      ) : (
        <motion.div key={selDate} variants={stagger} initial="hidden" animate="show" className="mt-3 space-y-3">
          {dayItems.length === 0 && <p className="text-sm text-brand-ink/40 italic py-6 text-center">{m.today_empty}</p>}
          {dayItems.map((b) => (
            <BookingCard key={`${b.type}-${b.id}`} b={b} statuses={statuses} canManage={canManage} onAction={act} />
          ))}
        </motion.div>
      )}

      {consentFor && (
        <ConsentScreen
          booking={consentFor}
          onDone={() => { setConsentFor(null); load(); }}
        />
      )}
    </div>
  );
};

export default TodayScreen;
